import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Autocomplete,
    Chip,
    Box,
    Typography,
    FormControlLabel,
    Switch,
    Tabs,
    Tab,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Alert,
    CircularProgress
} from '@mui/material';
import {
    Close as CloseIcon,
    Person as PersonIcon,
    Group as GroupIcon,
    Public as PublicIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';
import authService from '../services/auth.service';

interface DocumentSharingProps {
    open: boolean;
    onClose: () => void;
    documentId: string;
    documentName: string;
    currentSharing?: {
        sharedWith: string[];
        sharedDepartments: string[];
        public: boolean;
    };
    onUpdate: (sharing: any) => void;
}

interface User {
    _id: string;
    email: string;
    fullName: string;
    department?: string;
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`sharing-tabpanel-${index}`}
            aria-labelledby={`sharing-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ py: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

const DocumentSharing: React.FC<DocumentSharingProps> = ({
    open,
    onClose,
    documentId,
    documentName,
    currentSharing = { sharedWith: [], sharedDepartments: [], public: false },
    onUpdate
}) => {
    const [tabValue, setTabValue] = useState(0);
    const [users, setUsers] = useState<User[]>([]);
    const [departments, setDepartments] = useState<string[]>([]);
    const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
    const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
    const [isPublic, setIsPublic] = useState(currentSharing.public);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            fetchUsersAndDepartments();
            // Initialize with current sharing
            setIsPublic(currentSharing.public);
        }
    }, [open]);

    const fetchUsersAndDepartments = async () => {
        setLoading(true);
        try {
            const api = authService.getApiInstance();
            
            // Fetch users
            const usersResponse = await api.get('/users');
            const allUsers = usersResponse.data.users;
            
            // Filter to get currently shared users
            const sharedUsers = allUsers.filter((user: User) => 
                currentSharing.sharedWith.includes(user._id)
            );
            setSelectedUsers(sharedUsers);
            setUsers(allUsers);
            
            // Extract unique departments
            const uniqueDepts = Array.from(new Set(
                allUsers
                    .map((u: User) => u.department)
                    .filter(Boolean)
            )) as string[];
            setDepartments(uniqueDepts);
            
            // Set selected departments
            setSelectedDepartments(currentSharing.sharedDepartments);
        } catch (error) {
            console.error('Failed to fetch users and departments:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const api = authService.getApiInstance();
            
            const sharingData = {
                sharedWith: selectedUsers.map(u => u._id),
                sharedDepartments: selectedDepartments,
                public: isPublic
            };
            
            await api.patch(`/documents/${documentId}`, sharingData);
            onUpdate(sharingData);
            onClose();
        } catch (error) {
            console.error('Failed to update sharing:', error);
        } finally {
            setSaving(false);
        }
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const handleUserChange = (event: any, newValue: User[]) => {
        setSelectedUsers(newValue);
    };

    const handleDepartmentChange = (event: any, newValue: string[]) => {
        setSelectedDepartments(newValue);
    };

    const removeUser = (userId: string) => {
        setSelectedUsers(prev => prev.filter(u => u._id !== userId));
    };

    const removeDepartment = (dept: string) => {
        setSelectedDepartments(prev => prev.filter(d => d !== dept));
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h6">Share Document</Typography>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Typography variant="body2" color="text.secondary">
                    {documentName}
                </Typography>
            </DialogTitle>
            
            <DialogContent dividers>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <Tabs value={tabValue} onChange={handleTabChange} aria-label="sharing tabs">
                            <Tab icon={<PersonIcon />} label="Users" />
                            <Tab icon={<GroupIcon />} label="Departments" />
                            <Tab icon={<PublicIcon />} label="Public" />
                        </Tabs>

                        <TabPanel value={tabValue} index={0}>
                            <Autocomplete
                                multiple
                                options={users}
                                value={selectedUsers}
                                onChange={handleUserChange}
                                getOptionLabel={(option) => `${option.fullName} (${option.email})`}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="outlined"
                                        label="Select users to share with"
                                        placeholder="Type to search users..."
                                    />
                                )}
                                renderTags={() => null}
                            />
                            
                            {selectedUsers.length > 0 && (
                                <List sx={{ mt: 2 }}>
                                    {selectedUsers.map((user) => (
                                        <ListItem key={user._id} divider>
                                            <ListItemText
                                                primary={user.fullName}
                                                secondary={user.email}
                                            />
                                            <ListItemSecondaryAction>
                                                <IconButton
                                                    edge="end"
                                                    onClick={() => removeUser(user._id)}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </ListItemSecondaryAction>
                                        </ListItem>
                                    ))}
                                </List>
                            )}
                        </TabPanel>

                        <TabPanel value={tabValue} index={1}>
                            <Autocomplete
                                multiple
                                options={departments}
                                value={selectedDepartments}
                                onChange={handleDepartmentChange}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="outlined"
                                        label="Select departments to share with"
                                        placeholder="Type to search departments..."
                                    />
                                )}
                                renderTags={() => null}
                            />
                            
                            {selectedDepartments.length > 0 && (
                                <Box sx={{ mt: 2 }}>
                                    {selectedDepartments.map((dept) => (
                                        <Chip
                                            key={dept}
                                            label={dept}
                                            onDelete={() => removeDepartment(dept)}
                                            sx={{ m: 0.5 }}
                                        />
                                    ))}
                                </Box>
                            )}
                        </TabPanel>

                        <TabPanel value={tabValue} index={2}>
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                Making a document public will allow anyone in the organization to view it.
                            </Alert>
                            
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={isPublic}
                                        onChange={(e) => setIsPublic(e.target.checked)}
                                    />
                                }
                                label="Make this document public"
                            />
                            
                            {isPublic && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    This document will be visible to all users in the system.
                                </Typography>
                            )}
                        </TabPanel>
                    </>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={saving}
                >
                    {saving ? 'Saving...' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DocumentSharing;